import {useState, useCallback, useRef, useMemo, type ChangeEvent, type DragEvent} from 'react'
import {Accordion, AccordionDetails, Typography, Box, Button} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {setScreenImageUrl, selectActiveScene} from '../../../common/components/store/scenesSlice'
import {DEFAULT_SCREEN_IMAGE_URL} from '../../../common/constants/defaultAssets'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── Helpers ───────────────────────────────────────────────────────

const loadImageFile = (file: File): Promise<string> =>
  new Promise((resolve) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
    }
    reader.readAsDataURL(file)
  })

// ── ScreenImagePanel — screen image upload sub-accordion ──────────

const ScreenImagePanel = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()

  const screenImageUrl = useAppSelector((s) => selectActiveScene(s).screenImageUrl)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      const dataUrl = await loadImageFile(file)
      dispatch(setScreenImageUrl(dataUrl))
      e.target.value = ''
    },
    [dispatch],
  )

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  const handleDrop = useCallback(
    async (e: DragEvent) => {
      e.preventDefault()
      setDragOver(false)
      const file = e.dataTransfer.files[0]
      if (!file || !file.type.startsWith('image/')) return
      const dataUrl = await loadImageFile(file)
      dispatch(setScreenImageUrl(dataUrl))
    },
    [dispatch],
  )

  const isDefaultImage = screenImageUrl === DEFAULT_SCREEN_IMAGE_URL
  const sx = useMemo(() => buildStyles(tokens), [tokens])

  return (
    <Accordion disableGutters square sx={sx.subAccordion} defaultExpanded>
      <AccordionDetails sx={sx.subDetails}>
        {/* Drop zone + preview — show drop-here placeholder when the slot still
            holds the default image so users notice it's replaceable; the actual
            scene viewport keeps rendering `/sample.jpg` regardless. */}
        <Box
          sx={{...sx.dropZone, ...(dragOver ? sx.dropZoneActive : {})}}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isDefaultImage ? (
            <Box sx={sx.emptyPreview}>
              <UploadFileIcon sx={{fontSize: 24, opacity: 0.4}} />
              <Typography sx={sx.dropText}>{t('screenImage.dropHere')}</Typography>
            </Box>
          ) : (
            <Box component="img" src={screenImageUrl} alt={t('a11y.screenPreview')} sx={sx.imagePreview} />
          )}
          {dragOver && (
            <Box sx={sx.dropOverlay}>
              <Typography sx={sx.dropText}>{t('screenImage.dropHere')}</Typography>
            </Box>
          )}
        </Box>

        {/* Upload button */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<UploadFileIcon />}
          onClick={handleUploadClick}
          sx={sx.uploadButton}
        >
          {t('screenImage.upload')}
        </Button>
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} style={{display: 'none'}} />
      </AccordionDetails>
    </Accordion>
  )
}

// ── Styles ────────────────────────────────────────────────────────

const buildStyles = (tokens: ThemeTokens) => ({
  subAccordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    '&:before': {display: 'none'},
    '&.Mui-expanded': {margin: 0},
  },
  subSummary: {
    minHeight: 40,
    padding: '0 4px',
    '&.Mui-expanded': {minHeight: 40},
    '& .MuiAccordionSummary-content': {
      margin: '8px 0',
      alignItems: 'center',
      gap: '6px',
      '&.Mui-expanded': {margin: '8px 0'},
    },
  },
  expandIcon: {
    color: tokens.accordion.expandIcon,
    fontSize: 18,
  },
  subIcon: {
    color: tokens.accordion.sectionIcon,
    fontSize: 16,
  },
  subTitle: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    color: tokens.text.secondary,
  },
  subDetails: {
    padding: '4px 4px 12px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
    alignItems: 'stretch',
  },
  dropZone: {
    position: 'relative' as const,
    borderRadius: '6px',
    border: `2px dashed ${tokens.accordion.border}`,
    overflow: 'hidden',
    transition: 'border-color 0.2s',
    cursor: 'pointer',
  },
  dropZoneActive: {
    borderColor: tokens.text.primary,
  },
  imagePreview: {
    width: '100%',
    height: 120,
    objectFit: 'contain',
    display: 'block',
  },
  emptyPreview: {
    width: '100%',
    height: 120,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    color: tokens.text.muted,
  },
  dropOverlay: {
    position: 'absolute' as const,
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(2px)',
  },
  dropText: {
    fontSize: 12,
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  uploadButton: {
    fontSize: 11,
    textTransform: 'none' as const,
    color: tokens.text.secondary,
    borderColor: tokens.accordion.border,
    '&:hover': {
      borderColor: tokens.text.muted,
    },
  },
})

export default ScreenImagePanel
